import Hls from "hls.js";
// import "video.js/dist/video-js.css";

export default class HlsJSPlayer {
  constructor(options = {}) {
    this.options = options;
    this.playerDom = null;
    this.player = null;
    this.prevTime = 0;
  }

  supportsHLS() {
    return Boolean(
      this.playerDom.canPlayType("application/vnd.apple.mpegURL") ||
      this.playerDom.canPlayType("audio/mpegurl")
    );
  }

  mimeType(url) {
    if (url?.includes(".m3u8")) {
      if (this.supportsHLS()) {
        return "application/vnd.apple.mpegURL";
      }
      return "application/x-mpegURL";
    }
    if (url?.includes(".mpd")) {
      return "application/dash+xml";
    }
    if (url?.includes(".mp4")) {
      return "video/mp4";
    }
  }

  init() {
    this.createVideoDom();
    this.createHlsJSPlayer();
  }
  isIOSSafari() {
    const ua = window.navigator.userAgent;
    const isIOS = /iP(ad|hone|od)/.test(ua);
    const isWebKit = /WebKit/.test(ua);
    const isChrome = /CriOS/.test(ua); // Chrome on iOS
    const isFirefox = /FxiOS/.test(ua); // Firefox on iOS
    return isIOS && isWebKit && !isChrome && !isFirefox;
  }

  createVideoDom() {
    const { preload, muted, videoId, autoplay } = this.options;
    this.playerDom = document.createElement("video");
    this.playerDom.setAttribute("id", videoId);
    this.playerDom.setAttribute("class", "video-player");
    this.playerDom.controls = false;
    if (this.isIOSSafari()) {
      this.playerDom.setAttribute('playsinline', true);
      this.playerDom.setAttribute('webkit-playsinline', true);
    } 
    this.playerDom.autoplay = true;
    this.playerDom.preload = "auto";
    this.playerDom.style.position = "absolute";
    this.playerDom.style.left = 0;
    this.playerDom.style.top = 0;
    this.playerDom.style.width = "100%";
    this.playerDom.style.height = "100%";
    this.playerDom.style.zIndex = 1;
    this.playerDom.style.backgroundColor = "#000000";
    this.playerDom.crossOrigin = "anonymous";
    this.playerWrapper = document.getElementsByClassName("player-page")[0];
    this.playerWrapper.appendChild(this.playerDom);
  }

  createHlsJSPlayer = () => {
    const { url } = this.options;
    if (this.supportsHLS()) {
      this.playerDom.src = url;
      this._bindPlayerEvents();
    } else if (Hls.isSupported()) {
      this.player = new Hls();
      this.player.loadSource(url);
      this.player.attachMedia(this.playerDom);
      this.player.on(Hls.Events.MEDIA_ATTACHED, () => {
        this._bindPlayerEvents();
      });

    }
  };

  _bindPlayerEvents() {
    if (this.playerDom) {
      this.playerDom.onloadedmetadata = this._onLoadedMetaData;
      this.playerDom.onloadeddata = this._onLoadedData;
      this.playerDom.onplay = this._onPlay;
      this.playerDom.onpause = this._onPause;
      this.playerDom.ontimeupdate = this._onTimeUpdate;
      this.playerDom.onwaiting = this._onWaiting;
      this.playerDom.onseeking = this._onSeeking;
      this.playerDom.onseeked = this._onSeeked;
      this.playerDom.onplaying = this._onPlaying;
      this.playerDom.onended = this._onEnded;
      this.playerDom.onerror = this._onPlaybackError;
      this.playerDom.onloadstart = this._onLoadStart;
      this.playerDom.onprogress = this._onProgress;
      this.playerDom.onsuspend = this._onSuspend;
      this.playerDom.ondurationchange = this._onDurationChange;
      this.playerDom.onvolumechange = this._onVolumeChange;
      this.playerDom.onratechange = this._onRateChange;
      this.playerDom.onabort = this._onAbort;
      this.playerDom.onresize = this._onResize;
      this.playerDom.onstalled = this._onStalled;
    }
  }

  _onAddtrack = (e) => { };

  _unbindPlayerEvents = () => {
    if (this.playerDom) {
      this.playerDom.onloadedmetadata = null;
      this.playerDom.onloadeddata = null;
      this.playerDom.onplay = null;
      this.playerDom.onpause = null;
      this.playerDom.ontimeupdate = null;
      this.playerDom.onwaiting = null;
      this.playerDom.onseeking = null;
      this.playerDom.onseeked = null;
      this.playerDom.onplaying = null;
      this.playerDom.onended = null;
      this.playerDom.onerror = null;
      this.playerDom.onloadstart = null;
      this.playerDom.onprogress = null;
      this.playerDom.onsuspend = null;
      this.playerDom.ondurationchange = null;
      this.playerDom.onvolumechange = null;
      this.playerDom.onratechange = null;
      this.playerDom.onabort = null;
      this.playerDom.onresize = null;
      this.playerDom.onstalled = null;
      this.playerDom.oncuechange = null;
      this.playerDom = null;
    }
  };

  _onLoadedMetaData = (e) => {
    this.prevTime = 0;
    if (this.options?.callback) {
      this.options.callback("LOADEDMETADATA", e);
    }
  };

  _onLoadedData = (e) => {
    if (this.options?.callback) {
      this.options.callback(
        "LOADEDDATA",
        this.playerDom.currentTime,
        this.playerDom.duration
      );
    }
  };

  _handleStartupQuality = () => {
    let qualityLevels = this.getQualityLevels();
    if (!qualityLevels || qualityLevels.length === 0) {
      return;
    }
    if (!window.FAST_STARTUP) {
      return;
    }
    for (const element of qualityLevels) {
      if (element.height >= 360) {
        this.setQualityCapping(element.bitrate);
        break;
      }
    }
  };

  _onQualityChanged = (_event) => {
    try {
      let selectedQuality = this.getSelectedQualityLevel();
    } catch (e) {
      console.error("error qualityChanged", e);
    }
  };

  isPaused = () => {
    if (this.playerDom) {
      return this.playerDom.paused;
    }
  };

  isMuted = () => {
    if (this.player) {
      return this.player.muted();
    }
  };

  muted = (flag) => {
    if (this.player) {
      return this.player.muted(flag);
    }
  };

  _onPlay = (e) => {
    if (this.options?.callback) {
      this.options.callback("PLAY");
    }
  };

  _onPause = (e) => {
    if (this.options?.callback) {
      this.options.callback("PAUSE");
    }
  };

  _onTimeUpdate = (e) => {
    if (this.options?.callback) {
      if (
        parseInt(this.prevTime, 10) != parseInt(this.playerDom.currentTime, 10)
      ) {
        this.prevTime = parseInt(this.playerDom.currentTime, 10);
        this.options.callback(
          "TIMEUPDATE",
          this.playerDom.currentTime,
          this.playerDom.duration
        );
      }
    }
  };

  _onWaiting = (e) => {
    if (this.options?.callback) {
      this.options.callback("WAITING");
    }
  };

  _onSeeking = (e) => {
    if (this.options?.callback) {
      this.options.callback("SEEKING");
    }
  };

  _onSeeked = (e) => {
    if (this.options?.callback) {
      this.options.callback("SEEKED");
    }
  };

  _onPlaying = (e) => {
    if (this.options?.callback) {
      this.options.callback("PLAYING");
    }
  };

  _onEnded = (e) => {
    if (this.options?.callback) {
      this.options.callback("ENDED");
    }
  };

  _onPlaybackError = (e) => { };

  _onLoadStart = (e) => {
    if (this.options?.callback) {
      this.options.callback("LOADSTART");
    }
  };

  _onProgress = (e) => {
    if (this.options?.callback) {
      this.options.callback("PROGRESS");
    }
  };

  _onSuspend = (e) => {
    if (this.options?.callback) {
      this.options.callback("SUSPEND");
    }
  };

  _onDurationChange = (e) => {
    if (this.options?.callback) {
      this.options.callback("DURATIONCHANGE");
    }
  };

  _onVolumeChange = (e) => {
    if (this.options?.callback) {
      this.options.callback("VOLUMECHANGE");
    }
  };

  _onRateChange = (e) => {
    if (this.options?.callback) {
      this.options.callback("RATECHANGE");
    }
  };

  _onAbort = (e) => {
    if (this.options?.callback) {
      this.options.callback("ABORT");
    }
  };

  _onResize = (e) => {
    if (this.options?.callback) {
      this.options.callback("RESIZE");
    }
  };

  _onStalled = (e) => {
    if (this.options?.callback) {
      this.options.callback("STALLED");
    }
  };

  _onCuechange = () => {
    if (this.options?.callback) {
      this.options.callback("CUE_CHANGE");
    }
  };

  _onAudioChange = () => {
    if (this.options?.callback) {
      this.options.callback("AUDIOCHANGE");
    }
  };

  play() {
    if (this.playerDom) {
      let playPromise = this.playerDom.play();
      if (playPromise != undefined) {
        playPromise.then(() => {
          this.playerDom.play();
        }).catch((e) => console.error(e));
        return playPromise;
      }
    }
  }

  pause() {
    if (this.playerDom) {
      let playPromise = this.playerDom.pause();
      if (playPromise != undefined) {
        playPromise.then(() => {
          this.playerDom.pause();
        }).catch((e) => console.error(e));
        return playPromise;
      }
    }
  }

  setVolume(vol) {
    if (this.playerDom) {
      this.playerDom.volume = vol;
    }
  }

  forward(time) {
    if (this.playerDom && time != undefined) {
      this.playerDom.currentTime = this.playerDom.currentTime + time;
    }
  }

  rewind(time) {
    if (this.playerDom && time != undefined) {
      this.userInitiatedSeek = true;
      this.playerDom.currentTime = this.playerDom.currentTime - time;
    }
  }

  seek(time) {
    if (this.playerDom && time != undefined) {
      this.playerDom.currentTime = time;
    }
  }

  stop() {
    this.destroy();
  }

  destroy() {
    if (this.playerDom) {
      this.playerDom.pause();
      this.playerDom.removeAttribute("src");
      let source = this.playerDom.querySelector("source");
      if (source) {
        source.removeAttribute("src");
      }
      this.playerDom.load();
      this.playerDom.remove();
    }
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }
    this._unbindPlayerEvents();
  }

  currentTime() {
    if (this.playerDom) {
      return this.playerDom.currentTime;
    }
  }

  duration() {
    if (this.playerDom) {
      return this.playerDom.duration;
    }
  }


  getTextTracks() {
    if (this.player) {
      return this.player.textTracks();
    }
    if (this.playerDom) {
      return this.playerDom.textTracks;
    }
  }

  setTextTrack(_trackid) {
    /* TODO document why this method 'setSubtitleTrack' is empty */
  }

  addRemoteSubtitles() {
    //vjsPlayer.addRemoteTextTrack(Object options)
  }

  setDefaultVttSubtitle(subtitles, selectedSubtitle) {
    if (selectedSubtitle) {
      const subtitle = subtitles.filter((item) => {
        return item.language_short_name == selectedSubtitle;
      });
      if (subtitle && subtitle.length > 0) {
        if (document.querySelector("track")) {
          document.querySelector("track").remove();
        }
        let track = document.createElement("track");
        track.setAttribute("label", subtitle[0].language_name);
        track.setAttribute("kind", "subtitles");
        window.WebVTTConverter(subtitle[0].subtitle_path);
        window.WebVTTConverter.getURL().then(
          (bloburl) => {
            track.setAttribute("src", bloburl);
          },
          (err) => { }
        );
        track.setAttribute("srclang", subtitle[0].language_short_name);
        track.setAttribute("default", true);
        this.playerDom.appendChild(track);
      }
    } else {
      if (document.querySelector("track")) {
        document.querySelector("track").remove();
      }
    }
  }

  paused() {
    if (this.playerDom) {
      return this.playerDom.paused;
    }
  }
}
